import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, Heart, User, List, X, MagnifyingGlass } from "@phosphor-icons/react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger
} from "../components/ui/sheet";
import API from "../lib/api";

export default function Header() {
  const { user } = useAuth();
  const { cartCount, cartOpen, setCartOpen, cart, removeFromCart, updateCartItem } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();

  useEffect(() => {
    API.get("/categories")
      .then(r => setCategories(Array.isArray(r.data) ? r.data : []))
      .catch(err => console.error("Failed to load categories:", err));
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/shop", label: "Shop" },
    { path: "/about", label: "About" },
    ...(Array.isArray(categories) ? categories : []).slice(0, 3).map(cat => ({
      path: `/shop?category=${encodeURIComponent(cat.slug)}`,
      label: cat.name,
    })),
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    const [pathname, query] = path.split("?");
    if (pathname !== location.pathname) return false;
    const linkCat = query ? new URLSearchParams(query).get("category") : null;
    const currentCat = new URLSearchParams(location.search).get("category");
    return linkCat === currentCat;
  };

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-header">
      <div className="bg-[#D4AF37] text-black text-center py-1.5 px-4 text-[11px] tracking-[0.2em] uppercase font-medium" data-testid="free-shipping-banner">
        Free US Shipping on Orders Over $100
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#FDFBF7] hover:text-[#D4AF37] transition-colors duration-300"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} weight="thin" /> : <List size={24} weight="thin" />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <span className="font-['Cormorant_Garamond'] text-2xl md:text-3xl tracking-tighter font-light text-[#FDFBF7]">
              Lead<span className="text-[#D4AF37]">.</span>Serve<span className="text-[#D4AF37]">.</span>Slay
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`link-hover text-sm tracking-[0.15em] uppercase font-light transition-colors duration-300 ${
                  isActive(link.path) ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-[#FDFBF7]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-6">
            <Link to="/wishlist" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300" data-testid="wishlist-icon">
              <Heart size={22} weight="thin" />
            </Link>

            {user && user !== false ? (
              <Link
                to={user.role === "admin" ? "/admin" : "/account"}
                className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300"
                data-testid="account-icon"
              >
                <User size={22} weight="thin" />
              </Link>
            ) : (
              <Link to="/auth" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300" data-testid="login-icon">
                <User size={22} weight="thin" />
              </Link>
            )}

            {/* Cart Drawer */}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <button className="relative text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300" data-testid="cart-icon">
                  <ShoppingBag size={22} weight="thin" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent className="bg-[#121212] border-l border-[rgba(212,175,55,0.15)] w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7]">Shopping Bag</SheetTitle>
                </SheetHeader>
                <div className="mt-8 flex flex-col h-[calc(100vh-180px)]">
                  {cart.items.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-[#A1A1AA] text-sm tracking-wide">Your bag is empty</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                        {cart.items.map(item => (
                          <div key={item.product_id} className="flex gap-4 pb-6 border-b border-white/5" data-testid={`cart-item-${item.product_id}`}>
                            <div className="w-20 h-20 bg-[#1A1A1A] flex-shrink-0 overflow-hidden">
                              <img src={item.product?.image_url} alt={item.product?.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#FDFBF7] font-light truncate">{item.product?.name}</p>
                              <p className="text-[#D4AF37] text-sm mt-1">${item.product?.price?.toFixed(2)}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <button onClick={() => updateCartItem(item.product_id, item.quantity - 1)} className="text-[#A1A1AA] hover:text-[#FDFBF7] text-xs">-</button>
                                <span className="text-xs text-[#FDFBF7]">{item.quantity}</span>
                                <button onClick={() => updateCartItem(item.product_id, item.quantity + 1)} className="text-[#A1A1AA] hover:text-[#FDFBF7] text-xs">+</button>
                              </div>
                            </div>
                            <button onClick={() => removeFromCart(item.product_id)} className="text-[#A1A1AA] hover:text-red-400 transition-colors" data-testid={`remove-cart-item-${item.product_id}`}>
                              <X size={16} weight="thin" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="pt-6 border-t border-white/5 space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-[#A1A1AA] tracking-wide uppercase">Total</span>
                          <span className="text-lg text-[#D4AF37] font-['Cormorant_Garamond']">${cart.total?.toFixed(2)}</span>
                        </div>
                        <Link
                          to="/cart"
                          onClick={() => setCartOpen(false)}
                          className="block w-full bg-[#D4AF37] text-black text-center py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300"
                          data-testid="view-bag-btn"
                        >
                          View Bag
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0a0a0a] border-t border-white/5 animate-fade-in" data-testid="mobile-nav">
          <nav className="px-6 py-6 space-y-4">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block text-sm tracking-[0.15em] uppercase font-light ${
                  isActive(link.path) ? "text-[#D4AF37]" : "text-[#A1A1AA]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
